import { c as _c } from "react/compiler-runtime";
import { skipToken, useMutation } from '@tanstack/react-query';
import { getUrlSubdomain } from 'utils/domains';
import { MutationConfig, QueryConfig, useQuery } from '../../reactQuery';
import { accountIdentificationProtocols, fetchSsoInitiate, LoginBody, PasswordResetBody, requestOTP, requestPasswordReset, resendOTP, resetPassword, validateOTP } from './api';
export const ssoQueryKeys = {
  all: ['authentication', 'sso'] as const,
  initiate: (subdomain: string) => [...ssoQueryKeys.all, 'initiate', subdomain] as const
};

// Enterprise configuration, so it effectively never changes within a session. The login screen
// mounts from several entry points (/auth, invitations, surveys); without these the probe refetches
// on each one and re-shows the spinner that gates the form.
const SSO_INITIATE_STALE_TIME_MS = 60 * 60 * 1000;
const SSO_INITIATE_GC_TIME_MS = 24 * 60 * 60 * 1000;

/**
 * Pre-auth gate for the login screen: whether this subdomain's enterprise has WorkOS SSO on.
 *
 * The subdomain is read straight off the URL rather than taken from `useSubdomain()`, so it runs
 * in parallel with `/subdomains` instead of queued behind it — the value is known from the URL
 * either way, so the dependency would buy nothing, and SSO doesn't hinge on that lookup resolving.
 */
export const useSsoInitiate = options => {
  const $ = _c(5);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const subdomain = getUrlSubdomain();
    t1 = useQuery;
    t0 = ssoQueryKeys.initiate(subdomain || "");
    t2 = subdomain ? async () => fetchSsoInitiate(subdomain) : skipToken;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== options) {
    t3 = {
      queryKey: t0,
      queryFn: t2,
      staleTime: SSO_INITIATE_STALE_TIME_MS,
      gcTime: SSO_INITIATE_GC_TIME_MS,
      ...options
    };
    $[3] = options;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t1(t3);
};
export const useRequestPasswordReset = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["forgot-password"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const usePasswordReset = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["reset-password"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp2,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
type IdentificationProtocolsResponse = Awaited<ReturnType<typeof accountIdentificationProtocols>>;
export const useIdentificationProtocols = (email, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== email) {
    t0 = ["authentication", "protocols", email];
    t1 = async () => accountIdentificationProtocols(email);
    $[0] = email;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useIdentificationProtocolsMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["authentication", "protocols", "email"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp3,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useRequestOTP = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["authentication", "otp", "request"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp4,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useValidateOTP = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["authentication", "otp", "validate"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp5,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useResendOTP = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["authentication", "otp", "resend"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp6,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
function _temp(user) {
  return requestPasswordReset(user);
}
function _temp2(auth) {
  return resetPassword(auth);
}
async function _temp3(t0) {
  const {
    email
  } = t0;
  return accountIdentificationProtocols(email);
}
async function _temp4(t0) {
  const {
    email
  } = t0;
  return requestOTP(email);
}
async function _temp5(t0) {
  const {
    email,
    otp
  } = t0;
  return validateOTP(email, otp);
}
async function _temp6(t0) {
  const {
    email
  } = t0;
  return resendOTP(email);
}